/**
 * Phase 3J.8B · V2 Inventory Verification (Stock Book + Register + Ledger + Reconciliation).
 *
 * Reads exclusively from the Central Stock Engine V2 read APIs — no legacy
 * fallback. Owner-facing verification UI used to confirm every V2 posting
 * lands at the correct physical warehouse before the first live production
 * flow.
 *
 * APIs consumed:
 *   GET /api/inventory/v2/stock-book?domain=RM|SFG|FG&…
 *   GET /api/inventory/v2/stock-register?domain=…&from_date=…&to_date=…&…
 *   GET /api/inventory/v2/stock-ledger?domain=…&…
 *   GET /api/inventory/v2/reconcile?domain=…
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { exportToCSV } from "@/lib/csv";
import { StockReportTab } from "@/components/stock-report/StockReportTab";

const DOMAINS = [
  { key: "RM",      label: "Raw Material",   hint: "Inputs at RM Store & floors" },
  { key: "SFG",     label: "Semi-Finished",  hint: "SFW · SFC · SFSW" },
  { key: "FG",      label: "Finished Goods", hint: "Packed & dispatch-ready" },
  { key: "WASTAGE", label: "Wastage",        hint: "Central Wastage Warehouse · physical waste stock" },
];

const TABS = [
  { key: "book",     label: "Stock Book",     hint: "What do I have now and where?" },
  { key: "register", label: "Stock Register", hint: "What moved during this period?" },
  { key: "ledger",   label: "Stock Ledger",   hint: "Every transaction, chronological" },
  { key: "reconcile",label: "Reconciliation", hint: "Ledger vs Balance cache" },
  { key: "report",   label: "Stock Report",   hint: "Godown-wise item stock reconciliation" },
];
const VALID_TAB_KEYS = new Set(TABS.map(t => t.key));

const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono focus:outline-none focus:border-[var(--accent)]";
const fmtQty = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtDate = (v) => v ? String(v).slice(0, 19).replace("T", " ") : "—";

// Universal row search · matches any text/number value in the row (case-insensitive).
const rowText = (v) => v == null ? "" : typeof v === "object" ? Object.values(v).map(rowText).join(" ") : String(v);
const filterRows = (rows, q) => {
  const t = (q || "").trim().toLowerCase();
  return t ? rows.filter(r => rowText(r).toLowerCase().includes(t)) : rows;
};

function SearchBox({ value, onChange, testId }) {
  return (
    <div className="md:col-span-6 flex items-center gap-2">
      <input data-testid={testId} value={value} onChange={e => onChange(e.target.value)}
        placeholder="Search material, batch / lot, warehouse, document…" className={inp} />
      {value && (
        <button type="button" data-testid={`${testId}-clear`} onClick={() => onChange("")}
          className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm">Clear</button>
      )}
    </div>
  );
}

function DirBadge({ dir }) {
  const map = {
    IN:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    OUT: "bg-red-500/15 text-red-300 border-red-500/30",
  };
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${map[dir] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/30"}`}>{dir || "—"}</span>;
}

function DomainBadge({ d }) {
  const map = {
    RM:      "bg-sky-500/15 text-sky-300 border-sky-500/30",
    SFG:     "bg-amber-500/15 text-amber-300 border-amber-500/30",
    FG:      "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    WASTAGE: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  };
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${map[d] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/30"}`}>{d || "—"}</span>;
}

function DomainToggle({ value, onChange }) {
  return (
    <div className="inline-flex border border-[var(--border)] rounded-sm overflow-hidden" data-testid="v2-domain-toggle">
      {DOMAINS.map((d) => {
        const active = value === d.key;
        return (
          <button
            key={d.key}
            type="button"
            data-testid={`v2-domain-${d.key.toLowerCase()}`}
            onClick={() => onChange(d.key)}
            className={`h-9 px-4 text-xs font-mono uppercase tracking-widest transition-colors ${active ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
            title={d.hint}
          >
            {d.key}
            <span className="ml-1.5 text-[10px] opacity-70 hidden md:inline">{d.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Stock Book Tab (Phase 3J.8I · dynamic-godown pivot) ──────────────────
function StockBookTab({ domain, items, godowns, search, onSearch }) {
  const [warehouseId, setWarehouseId] = useState("");
  const [itemId, setItemId] = useState("");
  const [batch, setBatch] = useState("");
  const [includeZero, setIncludeZero] = useState(false);
  const [hideZeroCols, setHideZeroCols] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const run = async () => {
    setLoading(true);
    try {
      // Fetch every warehouse's balance for the current domain — the pivot
      // is performed client-side so we can group (item × batch × quality)
      // and expose each warehouse as a horizontal column.
      const { data } = await api.get("/inventory/v2/stock-book", {
        params: { domain, item_id: itemId || undefined,
                  include_zero: includeZero ? true : undefined }
      });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stock Book load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [domain, includeZero, itemId]);

  // Pivot: group raw warehouse-balance rows into one row per
  // (item_id × batch_lot × quality_status). Dynamic warehouse columns are
  // derived from the response so we never hard-code godown names.
  const { pivotRows, warehouseCols } = useMemo(() => {
    const src = data?.rows || [];
    const needle = batch.trim().toLowerCase();
    const filtered = needle
      ? src.filter(r => (r.batch_lot || "").toLowerCase().includes(needle))
      : src;
    const whFilter = warehouseId
      ? filtered.filter(r => r.warehouse_id === warehouseId)
      : filtered;
    // Group key = item_id + batch_lot + quality_status. Item code / name
    // used only for display; different batches / quality states stay as
    // separate rows per spec §7.
    const groups = new Map();
    const whMeta = new Map();
    for (const r of whFilter) {
      const key = [r.item_id || "", r.batch_lot || "", r.quality_status || ""].join("|");
      let g = groups.get(key);
      if (!g) {
        g = {
          key,
          item_id: r.item_id, item_code: r.item_code, item_name: r.item_name,
          batch_lot: r.batch_lot || "", quality_status: r.quality_status || "",
          domain: r.stock_domain || domain, uom: r.uom || "",
          buckets: {},   // { warehouse_id: current_balance }
          total: 0,
        };
        groups.set(key, g);
      }
      const bal = Number(r.current_balance || 0);
      g.buckets[r.warehouse_id || ""] = (g.buckets[r.warehouse_id || ""] || 0) + bal;
      g.total += bal;
      if (!whMeta.has(r.warehouse_id || "")) {
        whMeta.set(r.warehouse_id || "", {
          id: r.warehouse_id || "",
          name: r.warehouse_name || (r.warehouse_id ? r.warehouse_id.slice(0, 8) : "<blank>"),
          code: r.warehouse_code || "",
          kind: r.warehouse_kind || "",
        });
      }
    }
    // Warehouse columns: eligible for the domain, sorted by code (fallback name).
    let cols = Array.from(whMeta.values()).sort((a, b) =>
      (a.code || a.name).localeCompare(b.code || b.name));
    // Hide-Zero-Locations: drop columns whose every visible group balance
    // is zero AND has no negative value (negatives always stay visible §16).
    if (hideZeroCols && !warehouseId) {
      cols = cols.filter(c => Array.from(groups.values()).some(g => {
        const v = g.buckets[c.id] || 0;
        return Math.abs(v) > 1e-6 || v < 0;
      }));
    }
    const rows = Array.from(groups.values())
      // Drop fully zero rows unless Include Zero is on.
      .filter(g => includeZero || Math.abs(g.total) > 1e-6 || g.total < 0)
      .sort((a, b) => (a.item_code || "").localeCompare(b.item_code || "") ||
                       (a.batch_lot || "").localeCompare(b.batch_lot || ""));
    return { pivotRows: rows, warehouseCols: cols };
  }, [data, batch, warehouseId, hideZeroCols, includeZero, domain]);

  const grandTotal = pivotRows.reduce((s, r) => s + r.total, 0);
  const shownRows = useMemo(() => filterRows(pivotRows, search), [pivotRows, search]);

  const exportCsv = () => {
    if (!pivotRows.length) return;
    const rows = pivotRows.map(g => {
      const flat = {
        item_code: g.item_code, item_name: g.item_name,
        batch_lot: g.batch_lot, quality: g.quality_status,
      };
      warehouseCols.forEach(c => { flat[c.code || c.name] = Number(g.buckets[c.id] || 0).toFixed(3); });
      flat.total_stock = Number(g.total).toFixed(3);
      flat.uom = g.uom;
      return flat;
    });
    const headers = [
      { key: "item_code", label: "Material Code" },
      { key: "item_name", label: "Description" },
      { key: "batch_lot", label: "Batch / Lot" },
      { key: "quality",   label: "Quality" },
      ...warehouseCols.map(c => ({ key: c.code || c.name, label: c.name })),
      { key: "total_stock", label: "Total Stock" },
      { key: "uom",         label: "UOM" },
    ];
    exportToCSV(`v2-stock-book-${domain}-pivot.csv`, rows, headers);
  };

  const qtyCell = (v) => {
    const n = Number(v || 0);
    if (Math.abs(n) < 1e-6) return <span className="text-zinc-600">—</span>;
    return <span className={n < 0 ? "text-red-400 font-semibold" : "text-white"}>{fmtQty(n)}</span>;
  };

  return (
    <div className="space-y-3" data-testid="v2-stock-book-tab">
      <div className="tactical-card p-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Material</label>
          <select data-testid="v2-book-item" value={itemId} onChange={e => setItemId(e.target.value)} className={inp}>
            <option value="">All items</option>
            {items.map(i => <option key={i.id} value={i.id}>{i.code} · {i.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Warehouse / Godown</label>
          <select data-testid="v2-book-warehouse" value={warehouseId} onChange={e => setWarehouseId(e.target.value)} className={inp}>
            <option value="">All warehouses</option>
            {godowns.map(g => <option key={g.id} value={g.id}>{g.code || g.name} · {g.kind}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Batch / Lot</label>
          <input data-testid="v2-book-batch" value={batch} onChange={e => setBatch(e.target.value)} placeholder="filter batch" className={inp} />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-[11px] text-zinc-400 font-mono">
            <input data-testid="v2-book-zero" type="checkbox" checked={includeZero} onChange={e => setIncludeZero(e.target.checked)} />
            Include Zero Stock
          </label>
          <label className="inline-flex items-center gap-2 text-[11px] text-zinc-400 font-mono">
            <input data-testid="v2-book-hide-zero-cols" type="checkbox" checked={hideZeroCols} onChange={e => setHideZeroCols(e.target.checked)} />
            Hide Zero Locations
          </label>
        </div>
        <SearchBox value={search} onChange={onSearch} testId="v2-book-search" />
        <div className="md:col-span-6 flex items-center gap-2">
          <button data-testid="v2-book-run" onClick={run} disabled={loading}
            className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button data-testid="v2-book-export" onClick={exportCsv} disabled={!pivotRows.length}
            className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
            Export CSV
          </button>
          <div className="ml-auto text-[11px] text-zinc-500 font-mono">
            {pivotRows.length} row(s) · {warehouseCols.length} location col(s) · Grand total <span className="text-white">{fmtQty(grandTotal)}</span>
          </div>
        </div>
      </div>

      <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left sticky left-0 bg-[var(--bg-2)]/95 z-10">Material Code</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Batch / Lot</th>
              <th className="px-3 py-2 text-left">Quality</th>
              {warehouseCols.map(c => (
                <th key={c.id} className="px-3 py-2 text-right whitespace-nowrap" title={`${c.name} · ${c.kind}`} data-testid={`v2-book-col-${c.code || c.id}`}>
                  <div className="text-white">{c.code || c.name}</div>
                  <div className="text-[10px] normal-case tracking-normal text-zinc-500">{c.kind}</div>
                </th>
              ))}
              <th className="px-3 py-2 text-right whitespace-nowrap bg-[var(--bg-2)]/70">Total Stock</th>
              <th className="px-3 py-2 text-left">UOM</th>
              <th className="px-3 py-2 text-left">Trace</th>
            </tr>
          </thead>
          <tbody>
            {!loading && shownRows.length === 0 && (
              <tr><td colSpan={warehouseCols.length + 7} data-testid="v2-book-empty" className="text-center py-8 text-zinc-500 text-xs">No V2 stock available for selected filters.</td></tr>
            )}
            {shownRows.map((g, i) => (
              <tr key={g.key} data-testid={`v2-book-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs whitespace-nowrap sticky left-0 bg-[var(--bg-1)] group-hover:bg-[var(--bg-2)]">{g.item_code}</td>
                <td className="px-3 py-2 text-xs text-zinc-300">{g.item_name || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-purple-300">{g.batch_lot || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-zinc-300">{g.quality_status || "—"}</td>
                {warehouseCols.map(c => (
                  <td key={c.id} className="px-3 py-2 text-right font-mono tabular-nums" data-testid={`v2-book-cell-${i}-${c.code || c.id}`}>
                    {qtyCell(g.buckets[c.id])}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-white bg-[var(--bg-2)]/40" data-testid={`v2-book-total-${i}`}>{fmtQty(g.total)}</td>
                <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{g.uom}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    data-testid={`v2-book-trace-${i}`}
                    onClick={() => nav(`/material-traceability?kind=${domain}&id=${g.item_id}`)}
                    className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
                  >Trace →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Stock Register Tab ────────────────────────────────────────────────────
function StockRegisterTab({ domain, items, godowns, search, onSearch }) {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [itemId, setItemId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [includeReversed, setIncludeReversed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/v2/stock-register", {
        params: { domain, from_date: fromDate || undefined, to_date: toDate || undefined,
                  item_id: itemId || undefined, warehouse_id: warehouseId || undefined,
                  include_reversed: includeReversed ? true : undefined }
      });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stock Register load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [domain, includeReversed]);

  // Phase 3J.16C.1 · derive pure Receipts by subtracting the transfer /
  // production / other-IN sub-categories from the aggregate `inward` field.
  // Backend keeps `inward = receipts + transfer_in + production_in + other_in`.
  const receiptsOf = (r) => Math.max(0, Math.round(
    ((r.inward || 0) - (r.transfer_in || 0) - (r.production_in || 0) - (r.other_in || 0)) * 10000
  ) / 10000);

  const rows = data?.rows || [];
  const shownRows = filterRows(rows, search);
  const isWastage = domain === "WASTAGE";
  const totals = rows.reduce((a, r) => ({
    opening:          a.opening          + (r.opening || 0),
    receipts:         a.receipts         + receiptsOf(r),
    transfer_in:      a.transfer_in      + (r.transfer_in || 0),
    production_in:    a.production_in    + (r.production_in || 0),
    consumption:      a.consumption      + (r.consumption || 0),
    wastage:          a.wastage          + (r.wastage_generated || 0),
    transfer_out:     a.transfer_out     + (r.transfer_out || 0),
    wastage_received: a.wastage_received + (r.wastage_received || 0),
    other_in:         a.other_in         + (r.other_in || 0),
    other_out:        a.other_out        + (r.other_out || 0),
    inward:           a.inward           + (r.inward || 0),
    outward:          a.outward          + (r.outward || 0),
    closing:          a.closing          + (r.closing || 0),
  }), {
    opening: 0, receipts: 0, transfer_in: 0, production_in: 0,
    consumption: 0, wastage: 0, transfer_out: 0, wastage_received: 0,
    other_in: 0, other_out: 0, inward: 0, outward: 0, closing: 0,
  });

  const exportCsv = () => {
    if (!rows.length) return;
    const enriched = rows.map(r => ({ ...r, receipts: receiptsOf(r) }));
    if (isWastage) {
      // Phase 3J.16D-4 · WASTAGE-domain register export.
      exportToCSV(`v2-stock-register-wastage.csv`, enriched, [
        { key: "item_code", label: "Source Item Code" },
        { key: "item_name", label: "Source Item Description" },
        { key: "warehouse_name", label: "Central Wastage Warehouse" },
        { key: "opening", label: "Opening" },
        { key: "wastage_received", label: "Wastage Received" },
        { key: "other_in", label: "Other +" },
        { key: "other_out", label: "Other −" },
        { key: "closing", label: "Closing" },
        { key: "uom", label: "UOM" },
        { key: "movement_count", label: "Moves" },
        { key: "last_movement", label: "Last Move" },
      ]);
      return;
    }
    exportToCSV(`v2-stock-register-${domain}.csv`, enriched, [
      { key: "item_code", label: "Material Code" },
      { key: "item_name", label: "Description" },
      { key: "warehouse_name", label: "Warehouse" },
      { key: "warehouse_department", label: "Department" },
      { key: "opening", label: "Opening" },
      { key: "receipts", label: "Receipts" },
      { key: "transfer_in", label: "Transfer In" },
      { key: "production_in", label: "Production In" },
      { key: "consumption", label: "Consumption" },
      { key: "wastage_generated", label: "Wastage" },
      { key: "transfer_out", label: "Transfer Out" },
      { key: "other_in", label: "Other +" },
      { key: "other_out", label: "Other −" },
      { key: "closing", label: "Closing" },
      { key: "uom", label: "UOM" },
      { key: "movement_count", label: "Moves" },
      { key: "last_movement", label: "Last Move" },
    ]);
  };

  return (
    <div className="space-y-3" data-testid="v2-stock-register-tab">
      <div className="tactical-card p-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">From Date</label>
          <input data-testid="v2-reg-from" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={inp} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">To Date</label>
          <input data-testid="v2-reg-to" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={inp} />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Material</label>
          <select data-testid="v2-reg-item" value={itemId} onChange={e => setItemId(e.target.value)} className={inp}>
            <option value="">All items</option>
            {items.map(i => <option key={i.id} value={i.id}>{i.code} · {i.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Warehouse</label>
          <select data-testid="v2-reg-warehouse" value={warehouseId} onChange={e => setWarehouseId(e.target.value)} className={inp}>
            <option value="">All warehouses</option>
            {godowns.map(g => <option key={g.id} value={g.id}>{g.code || g.name} · {g.kind}</option>)}
          </select>
        </div>
        <SearchBox value={search} onChange={onSearch} testId="v2-reg-search" />
        <div className="md:col-span-6 flex items-center gap-4 flex-wrap">
          <label className="inline-flex items-center gap-2 text-[11px] text-zinc-400 font-mono" title="Adds fully-reversed pairs and QC status transitions back into Inward/Outward totals (audit view). Default OFF shows only business movements.">
            <input data-testid="v2-reg-include-reversed" type="checkbox" checked={includeReversed} onChange={e => setIncludeReversed(e.target.checked)} />
            Include Reversed / Audit Movements
          </label>
          {includeReversed && <span data-testid="v2-reg-audit-badge" className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest">Audit View · Includes Reversed + QC Transitions</span>}
          <button data-testid="v2-reg-run" onClick={run} disabled={loading}
            className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button data-testid="v2-reg-export" onClick={exportCsv} disabled={!rows.length}
            className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
            Export CSV
          </button>
          {isWastage ? (
            <div className="ml-auto text-[11px] text-zinc-500 font-mono" title={`Other + ${fmtQty(totals.other_in)} · Other − ${fmtQty(totals.other_out)}`}>
              {rows.length} row(s) · Op <span className="text-zinc-300">{fmtQty(totals.opening)}</span>
              {" · "}Wast-Rcvd <span className="text-rose-300">{fmtQty(totals.wastage_received)}</span>
              {" · "}Other + <span className="text-emerald-300">{fmtQty(totals.other_in)}</span>
              {" · "}Other − <span className="text-red-300">{fmtQty(totals.other_out)}</span>
              {" · "}Cl <span className="text-white">{fmtQty(totals.closing)}</span>
            </div>
          ) : (
            <div className="ml-auto text-[11px] text-zinc-500 font-mono" title={`Other + ${fmtQty(totals.other_in)} · Other − ${fmtQty(totals.other_out)}`}>
              {rows.length} row(s) · Op <span className="text-zinc-300">{fmtQty(totals.opening)}</span>
              {" · "}Rcpt <span className="text-emerald-300">{fmtQty(totals.receipts)}</span>
              {" · "}T-In <span className="text-emerald-200">{fmtQty(totals.transfer_in)}</span>
              {" · "}Prod <span className="text-sky-300">{fmtQty(totals.production_in)}</span>
              {" · "}Cons <span className="text-red-300">{fmtQty(totals.consumption)}</span>
              {" · "}Wast <span className="text-amber-300">{fmtQty(totals.wastage)}</span>
              {" · "}T-Out <span className="text-red-200">{fmtQty(totals.transfer_out)}</span>
              {" · "}Cl <span className="text-white">{fmtQty(totals.closing)}</span>
            </div>
          )}
        </div>
      </div>

      {isWastage ? (
      <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Source Item Code</th>
              <th className="px-3 py-2 text-left">Source Description</th>
              <th className="px-3 py-2 text-left">Central Wastage Warehouse</th>
              <th className="px-3 py-2 text-right" title="Business opening WASTAGE balance before window">Opening</th>
              <th className="px-3 py-2 text-right text-rose-300" title="Wastage-Receipt movements posted via WTR into this warehouse">Wastage Received</th>
              <th className="px-3 py-2 text-right text-emerald-300" title="Other IN — adjustments">Other +</th>
              <th className="px-3 py-2 text-right text-red-300" title="Other OUT — disposal / adjustments (future)">Other −</th>
              <th className="px-3 py-2 text-right" title="Closing = Opening + Wastage Received + Other + − Other −">Closing</th>
              <th className="px-3 py-2 text-left">UOM</th>
              <th className="px-3 py-2 text-right">Moves</th>
              <th className="px-3 py-2 text-left">Last Move</th>
            </tr>
          </thead>
          <tbody>
            {!loading && shownRows.length === 0 && (
              <tr><td colSpan={11} data-testid="v2-reg-empty" className="text-center py-8 text-zinc-500 text-xs">No WASTAGE movement in the selected window.</td></tr>
            )}
            {shownRows.map((r, i) => (
              <tr key={i} data-testid={`v2-reg-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs whitespace-nowrap">{r.item_code}</td>
                <td className="px-3 py-2 text-xs text-zinc-300">{r.item_name || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="text-white">{r.warehouse_name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{r.warehouse_kind} · {r.warehouse_department || "—"}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300" data-testid={`v2-reg-row-${i}-opening`}>{fmtQty(r.opening)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-rose-300" data-testid={`v2-reg-row-${i}-wastage-received`}>{fmtQty(r.wastage_received)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300" data-testid={`v2-reg-row-${i}-other-in`}>{fmtQty(r.other_in)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300" data-testid={`v2-reg-row-${i}-other-out`}>{fmtQty(r.other_out)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-white" data-testid={`v2-reg-row-${i}-closing`}>{fmtQty(r.closing)}</td>
                <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{r.uom}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{r.movement_count}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{fmtDate(r.last_movement)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      ) : (
      <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Material Code</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Warehouse</th>
              <th className="px-3 py-2 text-right" title="Business opening balance before window">Opening</th>
              <th className="px-3 py-2 text-right text-emerald-400" title="Pure Receipts + Opening postings (excludes Transfer In & Production In)">Receipts</th>
              <th className="px-3 py-2 text-right text-emerald-300" title="Inbound department transfers">Transfer In</th>
              <th className="px-3 py-2 text-right text-sky-300" title="Conversion-Output / FG-Receipt produced into this warehouse">Production In</th>
              <th className="px-3 py-2 text-right text-red-300" title="Productive Consumption (Conversion-Consume)">Consumption</th>
              <th className="px-3 py-2 text-right text-amber-300" title="Wastage Generated at production (Conversion-Wastage)">Wastage</th>
              <th className="px-3 py-2 text-right text-red-200" title="Outbound department transfers / Issues / FG-Dispatch">Transfer Out</th>
              <th className="px-3 py-2 text-right" title="Closing = Opening + Inward − Outward (Inward = Receipts+T.In+Prod+Other+, Outward = Cons+Wastage+T.Out+Other−)">Closing</th>
              <th className="px-3 py-2 text-left">UOM</th>
              <th className="px-3 py-2 text-right">Moves</th>
              <th className="px-3 py-2 text-left">Last Move</th>
            </tr>
          </thead>
          <tbody>
            {!loading && shownRows.length === 0 && (
              <tr><td colSpan={14} data-testid="v2-reg-empty" className="text-center py-8 text-zinc-500 text-xs">No V2 movement in the selected window.</td></tr>
            )}
            {shownRows.map((r, i) => {
              const equation = Math.round((r.opening + r.inward - r.outward - r.closing) * 10000) / 10000;
              const bad = Math.abs(equation) > 0.001;
              const receipts = receiptsOf(r);
              const otherIn = r.other_in || 0;
              const otherOut = r.other_out || 0;
              const hasOther = Math.abs(otherIn) > 1e-6 || Math.abs(otherOut) > 1e-6;
              const otherTitle = hasOther ? `Other +: ${fmtQty(otherIn)} · Other −: ${fmtQty(otherOut)}` : "";
              return (
                <tr key={i} data-testid={`v2-reg-row-${i}`} className={`border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30 ${bad ? "bg-red-500/5" : ""}`}>
                  <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs whitespace-nowrap">{r.item_code}</td>
                  <td className="px-3 py-2 text-xs text-zinc-300">{r.item_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="text-white">{r.warehouse_name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{r.warehouse_kind} · {r.warehouse_department || "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300" data-testid={`v2-reg-row-${i}-opening`}>{fmtQty(r.opening)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-400" data-testid={`v2-reg-row-${i}-receipts`}>{fmtQty(receipts)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300" data-testid={`v2-reg-row-${i}-transfer-in`}>{fmtQty(r.transfer_in)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-sky-300" data-testid={`v2-reg-row-${i}-production-in`}>{fmtQty(r.production_in)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300" data-testid={`v2-reg-row-${i}-consumption`}>{fmtQty(r.consumption)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300" data-testid={`v2-reg-row-${i}-wastage`}>{fmtQty(r.wastage_generated)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-red-200" data-testid={`v2-reg-row-${i}-transfer-out`}>{fmtQty(r.transfer_out)}</td>
                  <td
                    className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-white"
                    data-testid={`v2-reg-row-${i}-closing`}
                    title={otherTitle || undefined}
                  >
                    {fmtQty(r.closing)}
                    {hasOther && <span className="ml-1 text-[10px] text-amber-300" title={otherTitle}>±</span>}
                    {bad && <span className="ml-1 text-red-400" title={`Opening+In−Out−Closing = ${equation}`}>!</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{r.uom}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{r.movement_count}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{fmtDate(r.last_movement)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// ─── Stock Ledger Tab ──────────────────────────────────────────────────────
function StockLedgerTab({ domain, items, godowns, search, onSearch }) {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [itemId, setItemId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const nav = useNavigate();

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/v2/stock-ledger", {
        params: { domain, from_date: fromDate || undefined, to_date: toDate || undefined,
                  item_id: itemId || undefined, warehouse_id: warehouseId || undefined, limit: 5000 }
      });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stock Ledger load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [domain]);

  const rows = data?.rows || [];
  const shownRows = filterRows(rows, search);
  const exportCsv = () => {
    if (!rows.length) return;
    exportToCSV(`v2-stock-ledger-${domain}.csv`, rows, [
      { key: "transaction_datetime", label: "Date / Time" },
      { key: "source_document_no", label: "Doc No" },
      { key: "source_document_type", label: "Doc Type" },
      { key: "transaction_type", label: "Txn" },
      { key: "item_code", label: "Material Code" },
      { key: "warehouse_name", label: "Warehouse" },
      { key: "batch_lot", label: "Batch/Lot" },
      { key: "in_qty", label: "In" },
      { key: "out_qty", label: "Out" },
      { key: "running_balance", label: "Running Balance" },
      { key: "uom", label: "UOM" },
      { key: "so_no", label: "SO" },
      { key: "cwo_no", label: "CWO" },
      { key: "posted_by", label: "Posted By" },
    ]);
  };

  return (
    <div className="space-y-3" data-testid="v2-stock-ledger-tab">
      <div className="tactical-card p-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">From Date</label>
          <input data-testid="v2-led-from" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={inp} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">To Date</label>
          <input data-testid="v2-led-to" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={inp} />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Material</label>
          <select data-testid="v2-led-item" value={itemId} onChange={e => setItemId(e.target.value)} className={inp}>
            <option value="">All items</option>
            {items.map(i => <option key={i.id} value={i.id}>{i.code} · {i.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Warehouse</label>
          <select data-testid="v2-led-warehouse" value={warehouseId} onChange={e => setWarehouseId(e.target.value)} className={inp}>
            <option value="">All warehouses</option>
            {godowns.map(g => <option key={g.id} value={g.id}>{g.code || g.name} · {g.kind}</option>)}
          </select>
        </div>
        <SearchBox value={search} onChange={onSearch} testId="v2-led-search" />
        <div className="md:col-span-6 flex items-center gap-2">
          <button data-testid="v2-led-run" onClick={run} disabled={loading}
            className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button data-testid="v2-led-export" onClick={exportCsv} disabled={!rows.length}
            className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
            Export CSV
          </button>
          <div className="ml-auto text-[11px] text-zinc-500 font-mono">
            {rows.length} txn(s) · Closing <span className="text-white">{fmtQty(data?.closing_balance)}</span>
          </div>
        </div>
      </div>

      <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left">Date / Time</th>
              <th className="px-3 py-2 text-left">Doc No</th>
              <th className="px-3 py-2 text-left">Doc Type</th>
              <th className="px-3 py-2 text-left">Txn</th>
              <th className="px-3 py-2 text-left">Material</th>
              <th className="px-3 py-2 text-left">Warehouse</th>
              <th className="px-3 py-2 text-left">Batch / Lot</th>
              <th className="px-3 py-2 text-right">In</th>
              <th className="px-3 py-2 text-right">Out</th>
              <th className="px-3 py-2 text-right">Running</th>
              <th className="px-3 py-2 text-left">SO / CWO</th>
              <th className="px-3 py-2 text-left">Group</th>
              <th className="px-3 py-2 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && shownRows.length === 0 && (
              <tr><td colSpan={13} data-testid="v2-led-empty" className="text-center py-8 text-zinc-500 text-xs">No V2 transactions in the selected filters.</td></tr>
            )}
            {shownRows.map((r, i) => (
              <tr key={r.transaction_id || i} data-testid={`v2-led-row-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                <td className="px-3 py-2 font-mono text-[11px] text-zinc-300 whitespace-nowrap">{fmtDate(r.posted_at || r.transaction_datetime || r.transaction_date || r.created_at)}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-[var(--accent)]">{r.source_document_no || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-zinc-400">{r.source_document_type || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <DirBadge dir={r.direction} />
                    <span className="text-[10px] font-mono text-zinc-400">{r.transaction_type}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-mono text-[var(--accent)]">{r.item_code}</div>
                  <div className="text-[10px] text-zinc-500">{r.item_name}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="text-white">{r.warehouse_name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{r.warehouse_kind}</div>
                </td>
                <td className="px-3 py-2 font-mono text-[10px] text-purple-300">{r.batch_lot || "—"}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{r.in_qty ? fmtQty(r.in_qty) : ""}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300">{r.out_qty ? fmtQty(r.out_qty) : ""}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-white">{fmtQty(r.running_balance)}</td>
                <td className="px-3 py-2 text-[10px] font-mono">
                  {r.so_no && <div className="text-amber-300">{r.so_no}</div>}
                  {r.cwo_no && <div className="text-sky-300">{r.cwo_no}</div>}
                  {!r.so_no && !r.cwo_no && <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-3 py-2 text-[10px] font-mono">
                  {r.transfer_pair_id && <div className="text-sky-300" title={`Transfer pair ${r.transfer_pair_id}`}>TRANSFER</div>}
                  {r.conversion_group_id && <div className="text-amber-300" title={`Conversion ${r.conversion_group_id}`}>CONV</div>}
                  {r.reversal_of_id && <div className="text-red-300" title={`Reversal of ${r.reversal_of_id}`}>REV</div>}
                  {!r.transfer_pair_id && !r.conversion_group_id && !r.reversal_of_id && <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    data-testid={`v2-led-details-${i}`}
                    onClick={() => setDetailRow(r)}
                    className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-[var(--accent)]"
                  >Details</button>
                  <button
                    type="button"
                    data-testid={`v2-led-trace-${i}`}
                    onClick={() => nav(`/material-traceability?kind=${domain}&id=${r.item_id}`)}
                    className="ml-2 text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
                  >Trace</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detailRow && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="v2-led-drawer">
          <div className="fixed inset-0 bg-black/60" onClick={() => setDetailRow(null)} />
          <div className="relative w-full max-w-md bg-[var(--bg-1)] border-l border-[var(--border)] h-full overflow-y-auto p-5 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline mb-1">V2 Transaction · Technical Details</div>
                <h2 className="font-mono text-sm text-white break-all">{detailRow.transaction_id}</h2>
              </div>
              <button data-testid="v2-led-drawer-close" onClick={() => setDetailRow(null)} className="h-8 w-8 rounded-sm border border-[var(--border)] text-zinc-400 hover:text-white hover:border-[var(--accent)] font-mono">×</button>
            </div>
            <div className="space-y-1 text-xs font-mono">
              {[
                ["transaction_id", detailRow.transaction_id],
                ["transaction_type", detailRow.transaction_type],
                ["direction", detailRow.direction],
                ["stock_domain", detailRow.stock_domain],
                ["posted_at (Date / Time)", detailRow.posted_at || detailRow.transaction_datetime],
                ["transaction_date (business date)", detailRow.transaction_date],
                ["created_at", detailRow.created_at],
                ["item_id", detailRow.item_id],
                ["item_code", detailRow.item_code],
                ["warehouse_id", detailRow.warehouse_id],
                ["warehouse_name", detailRow.warehouse_name],
                ["batch_lot", detailRow.batch_lot],
                ["quality_status", detailRow.quality_status],
                ["uom", detailRow.uom],
                ["source_document_type", detailRow.source_document_type],
                ["source_document_id", detailRow.source_document_id],
                ["source_document_no", detailRow.source_document_no],
                ["posting_link_id", detailRow.posting_link_id],
                ["transfer_pair_id", detailRow.transfer_pair_id],
                ["conversion_group_id", detailRow.conversion_group_id],
                ["reversal_of_id", detailRow.reversal_of_id],
                ["so_no", detailRow.so_no],
                ["cwo_no", detailRow.cwo_no],
                ["wo_no", detailRow.wo_no],
                ["department", detailRow.department],
                ["posted_by", detailRow.posted_by],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2 border-b border-[var(--border)]/60 py-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</div>
                  <div className="col-span-2 text-zinc-200 break-all">{v || <span className="text-zinc-600">—</span>}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reconciliation Tab ────────────────────────────────────────────────────
const REC_CLASS_META = {
  MATCHED:             { label: "Matched",         cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  PHYSICAL_QTY_DRIFT:  { label: "Physical Drift",  cls: "border-red-500/40 bg-red-500/10 text-red-300" },
  QUALITY_BUCKET_DRIFT:{ label: "Quality-Bucket",  cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  UNSUPPORTED_REPLAY:  { label: "Unsupported",     cls: "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300" },
  HISTORICAL_NO_LEDGER:{ label: "Historical",      cls: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300" },
};
function RecClassBadge({ c, title }) {
  const m = REC_CLASS_META[c] || { label: c || "—", cls: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300" };
  return (
    <span title={title || m.label} data-testid={`v2-rec-class-${c}`}
      className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${m.cls}`}>
      {m.label}
    </span>
  );
}

function ReconciliationTab({ domain }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/v2/reconcile", { params: { domain } });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reconciliation failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [domain]);

  const drift = data?.drift_buckets || [];
  const matched = data?.matched_buckets || [];

  return (
    <div className="space-y-3" data-testid="v2-reconcile-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="v2-rec-run" onClick={run} disabled={loading}
          className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">
          {loading ? "Reconciling…" : "Reconcile Now"}
        </button>
        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-sky-500/40 bg-sky-500/10 text-sky-300" title="This action only replays the canonical V2 ledger and compares it to the balance cache. It never writes, adjusts, or repairs any stock, balance, or QC record.">Read-Only · Replay & Compare</span>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1 min-w-[280px]">
          <div className="tactical-card p-3 border-l-2 border-l-red-500">
            <div className="overline mb-1">Physical Drift</div>
            <div className="font-mono text-2xl text-red-400" data-testid="v2-rec-physical-count">{data?.physical_qty_drift_count ?? "—"}</div>
          </div>
          <div className="tactical-card p-3 border-l-2 border-l-amber-500">
            <div className="overline mb-1">Quality-Bucket</div>
            <div className="font-mono text-2xl text-amber-400" data-testid="v2-rec-quality-count">{data?.quality_bucket_drift_count ?? "—"}</div>
          </div>
          <div className="tactical-card p-3 border-l-2 border-l-fuchsia-500">
            <div className="overline mb-1">Unsupported</div>
            <div className="font-mono text-2xl text-fuchsia-400" data-testid="v2-rec-unsupported-count">{data?.unsupported_replay_count ?? "—"}</div>
          </div>
          <div className="tactical-card p-3 border-l-2 border-l-emerald-500">
            <div className="overline mb-1">Matched</div>
            <div className="font-mono text-2xl text-emerald-400" data-testid="v2-rec-matched-count">{data?.matched_bucket_count ?? "—"}</div>
          </div>
        </div>
      </div>

      <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Domain</th>
              <th className="px-3 py-2 text-left">Material</th>
              <th className="px-3 py-2 text-left">Warehouse</th>
              <th className="px-3 py-2 text-left">Batch / Lot</th>
              <th className="px-3 py-2 text-left">Quality</th>
              <th className="px-3 py-2 text-right">Ledger Qty</th>
              <th className="px-3 py-2 text-right">Balance Qty</th>
              <th className="px-3 py-2 text-right">Drift</th>
              <th className="px-3 py-2 text-left">UOM</th>
              <th className="px-3 py-2 text-left">Type</th>
            </tr>
          </thead>
          <tbody>
            {!loading && drift.length === 0 && matched.length === 0 && (
              <tr><td colSpan={11} data-testid="v2-rec-empty" className="text-center py-8 text-zinc-500 text-xs">No V2 balance rows available for reconciliation.</td></tr>
            )}
            {drift.map((r, i) => (
              <tr key={`d-${i}`} data-testid={`v2-rec-drift-${i}`} className="border-t border-[var(--border)] bg-red-500/5">
                <td className="px-3 py-2"><span className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 text-[10px] font-mono uppercase tracking-widest">Mismatch</span></td>
                <td className="px-3 py-2"><DomainBadge d={r.domain} /></td>
                <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs">{r.item_code}</td>
                <td className="px-3 py-2 text-xs text-white">{r.warehouse_name}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-purple-300">{r.batch_lot || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-zinc-300">{r.quality_status || "—"}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmtQty(r.ledger_sum)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmtQty(r.balance_cache)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-red-400">{fmtQty(r.drift)}</td>
                <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{r.uom}</td>
                <td className="px-3 py-2"><RecClassBadge c={r.classification} title={r.classification === "QUALITY_BUCKET_DRIFT" ? `Physical total matches (ledger ${fmtQty(r.group_physical_ledger)} = balance ${fmtQty(r.group_physical_balance)}); only the quality-bucket allocation differs.` : (r.classification === "UNSUPPORTED_REPLAY" ? (r.unsupported_reason || "Balance-affecting transaction whose semantics could not be replayed — fail-closed.") : (r.classification === "PHYSICAL_QTY_DRIFT" ? `Physical quantity mismatch (ledger total ${fmtQty(r.group_physical_ledger)} vs balance total ${fmtQty(r.group_physical_balance)}).` : ""))} /></td>
              </tr>
            ))}
            {matched.map((r, i) => (
              <tr key={`m-${i}`} data-testid={`v2-rec-match-${i}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30">
                <td className="px-3 py-2"><span className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">Matched</span></td>
                <td className="px-3 py-2"><DomainBadge d={r.domain} /></td>
                <td className="px-3 py-2 font-mono text-[var(--accent)] text-xs">{r.item_code}</td>
                <td className="px-3 py-2 text-xs text-white">{r.warehouse_name}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-purple-300">{r.batch_lot || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-zinc-300">{r.quality_status || "—"}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmtQty(r.ledger_sum)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmtQty(r.balance_cache)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-400">0.000</td>
                <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{r.uom}</td>
                <td className="px-3 py-2"><RecClassBadge c="MATCHED" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Root Page ─────────────────────────────────────────────────────────────
export default function V2Inventory() {
  const [domain, setDomain] = useState("RM");
  // PMS.V3 Inventory-V2 Phase 1 · router-aware tab state. The active tab is
  // DERIVED from the ?tab= query param so it reacts to sidebar deep-links and
  // in-page tab clicks WITHOUT a remount. Invalid/missing → "book".
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const tab = VALID_TAB_KEYS.has(rawTab) ? rawTab : "book";
  const [items, setItems] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [search, setSearch] = useState({ book: "", register: "", ledger: "" });
  const searchProps = (k) => ({ search: search[k], onSearch: (v) => setSearch(s => ({ ...s, [k]: v })) });

  useEffect(() => {
    (async () => {
      try {
        const [rm, sf, art, gs] = await Promise.all([
          api.get("/raw-materials/").catch(() => ({ data: [] })),
          api.get("/sf-master/").catch(() => ({ data: [] })),
          api.get("/articles/").catch(() => ({ data: [] })),
          api.get("/godowns/").catch(() => ({ data: [] })),
        ]);
        const rmItems = (rm.data || []).map(r => ({ id: r.id, code: r.code, name: r.name, domain: "RM" }));
        const sfgItems = (sf.data || []).map(s => ({ id: s.id, code: s.sf_code, name: s.description || s.process, domain: "SFG" }));
        const fgItems = (art.data || []).map(a => ({ id: a.id, code: a.code, name: a.name, domain: "FG" }));
        setItems([...rmItems, ...sfgItems, ...fgItems]);
        setGodowns((gs.data || []).filter(g => g.is_active !== false));
      } catch (_e) { /* silent */ }
    })();
  }, []);

  const domainItems = useMemo(
    () => items.filter(i => i.domain === domain),
    [items, domain]
  );
  const domainGodowns = useMemo(() => {
    if (domain === "RM") return godowns.filter(g => ["RM", "WIP"].includes(g.kind));
    if (domain === "SFG") return godowns.filter(g => ["WIP", "SFG"].includes(g.kind));
    if (domain === "FG") return godowns.filter(g => ["FG"].includes(g.kind));
    // Phase 3J.16D-4 · WASTAGE — Central Wastage Warehouse can live in
    // any kind (owner-configured); surface every active godown.
    if (domain === "WASTAGE") return godowns;
    return godowns;
  }, [godowns, domain]);

  const changeTab = (k) => {
    // Router-aware: update only ?tab=, preserving any other query params.
    const next = new URLSearchParams(searchParams);
    next.set("tab", k);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-6 space-y-4" data-testid="v2-inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Inventory · Reports</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Inventory Reports</h1>
          <p className="text-xs text-zinc-500 mt-1">Stock Book · Stock Register · Stock Ledger · Reconciliation — sourced strictly from the Central Stock Engine.</p>
        </div>
        <div className="flex items-center gap-3">
          <DomainToggle value={domain} onChange={setDomain} />
          <span data-testid="v2-source-indicator" className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">
            Inventory Source: V2
          </span>
        </div>
      </div>

      <div className="border-b border-[var(--border)] flex items-center gap-1 flex-wrap">
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`v2-tab-${t.key}`}
              type="button"
              onClick={() => changeTab(t.key)}
              className={`h-9 px-4 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${active ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
              title={t.hint}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "book"     && <StockBookTab       domain={domain} items={domainItems} godowns={domainGodowns} {...searchProps("book")} />}
      {tab === "register" && <StockRegisterTab   domain={domain} items={domainItems} godowns={domainGodowns} {...searchProps("register")} />}
      {tab === "ledger"   && <StockLedgerTab     domain={domain} items={domainItems} godowns={domainGodowns} {...searchProps("ledger")} />}
      {tab === "reconcile"&& <ReconciliationTab  domain={domain} />}
      {tab === "report"   && <StockReportTab     domain={domain} godowns={domainGodowns} />}
    </div>
  );
}
